import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  headerFormatToMediaCategory,
  whatsappMediaApi,
  type WhatsAppMediaItem,
} from '@/api/whatsappMedia';

export type CampaignHeaderMediaValue = {
  headerMediaLink: string;
  headerMediaId: string;
  libraryMediaId: string;
};

type Props = {
  headerFormat: string;
  templateName?: string;
  value: CampaignHeaderMediaValue;
  onChange: (next: CampaignHeaderMediaValue) => void;
  onSaveTemplateDefault?: () => void;
  savingDefault?: boolean;
  showSaveDefault?: boolean;
};

export function CampaignHeaderMediaPicker({
  headerFormat,
  templateName,
  value,
  onChange,
  onSaveTemplateDefault,
  savingDefault,
  showSaveDefault = true,
}: Props) {
  const category = headerFormatToMediaCategory(headerFormat);

  const mediaQuery = useQuery({
    queryKey: ['wa-media-library', category],
    queryFn: async () => {
      if (!category) return [] as WhatsAppMediaItem[];
      const res = await whatsappMediaApi.list({ category });
      return res.data.data.items;
    },
    enabled: !!category,
  });

  const items = mediaQuery.data ?? [];

  useEffect(() => {
    if (!templateName?.trim()) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await whatsappMediaApi.getTemplateDefault(templateName);
        const media = res.data.data.media;
        if (!cancelled && media) {
          onChange({
            headerMediaLink: media.publicUrl,
            headerMediaId: value.headerMediaId,
            libraryMediaId: media.id,
          });
        }
      } catch {
        // no default configured
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- prefill once per template
  }, [templateName]);

  const selectedItem = useMemo(
    () => items.find((m) => m.id === value.libraryMediaId) || null,
    [items, value.libraryMediaId]
  );

  if (!category) return null;

  return (
    <div className="rounded-md border p-3 space-y-3">
      <Label className="text-sm font-semibold">Header media when sending</Label>
      <p className="text-xs text-muted-foreground">
        Select from Media Library or paste a public HTTPS URL. Meta requires the same media on each
        campaign send.
      </p>

      <div className="space-y-2">
        <Label className="text-xs">Select existing media</Label>
        <Select
          value={value.libraryMediaId || '__none__'}
          onValueChange={(id) => {
            if (id === '__none__') {
              onChange({ ...value, libraryMediaId: '' });
              return;
            }
            const item = items.find((m) => m.id === id);
            if (item) {
              onChange({
                headerMediaLink: item.publicUrl,
                headerMediaId: value.headerMediaId,
                libraryMediaId: item.id,
              });
            }
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder={`Choose ${category} from library`} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">— Manual URL —</SelectItem>
            {items.map((m) => (
              <SelectItem key={m.id} value={m.id}>
                {m.originalName} ({Math.round(m.fileSize / 1024)} KB)
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {mediaQuery.isLoading && (
          <p className="text-xs text-muted-foreground">Loading media library…</p>
        )}
        {!mediaQuery.isLoading && items.length === 0 && (
          <p className="text-xs text-amber-700">
            No {category} files in library yet. Upload via CRM → WhatsApp Campaigns → Media Library.
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label className="text-xs">Public HTTPS URL</Label>
        <Input
          placeholder={`Public HTTPS ${category} URL`}
          value={value.headerMediaLink}
          onChange={(e) =>
            onChange({
              ...value,
              headerMediaLink: e.target.value,
              libraryMediaId:
                selectedItem && e.target.value === selectedItem.publicUrl
                  ? value.libraryMediaId
                  : '',
            })
          }
        />
      </div>

      <div className="space-y-2">
        <Label className="text-xs">WhatsApp media id (optional)</Label>
        <Input
          placeholder="From template upload (optional if URL is set)"
          value={value.headerMediaId}
          onChange={(e) => onChange({ ...value, headerMediaId: e.target.value })}
        />
      </div>

      {showSaveDefault &&
        onSaveTemplateDefault &&
        templateName &&
        (value.libraryMediaId || value.headerMediaLink.trim()) && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={savingDefault}
          onClick={onSaveTemplateDefault}
        >
          {savingDefault ? 'Saving…' : 'Save as template default'}
        </Button>
      )}
    </div>
  );
}
